// frontend/src/pages/DataWorkspacePage/AddConnectionModal.jsx
import React, { useState } from 'react';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';

const ENGINES = [
    { value: 'postgresql', type: 1, label: 'PostgreSQL', defaultPort: 5432 },
    { value: 'mysql',      type: 2, label: 'MySQL',      defaultPort: 3306 },
    { value: 'mssql',      type: 3, label: 'MS SQL Server', defaultPort: 1433 },
];

const Field = ({ label, children, required }) => (
    <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-400">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const Input = ({ ...props }) => (
    <input
        {...props}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/60 transition-colors"
    />
);

const AddConnectionModal = ({ token, onClose, onCreated }) => {
    const { addNotification } = useNotification();
    const [form, setForm] = useState({
        name: '', description: '', engine: 'postgresql',
        host: '', port: '5432', database: '', username: '', password: '',
    });
    const [testing, setTesting]   = useState(false);
    const [saving,  setSaving]    = useState(false);
    const [showPwd, setShowPwd]   = useState(false);

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const handleEngineChange = (engine) => {
        const meta = ENGINES.find(e => e.value === engine);
        set('engine', engine);
        set('port', String(meta?.defaultPort || 5432));
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const meta = ENGINES.find(e => e.value === form.engine);
            const payload = {
                name: form.name || 'TestConn',
                type: meta.type,
                host: form.host,
                port: Number(form.port) || meta.defaultPort,
                database: form.database,
                username: form.username,
                password: form.password
            };
            
            const testRes = await apiFetch(`/datasources/test`, token, { 
                method: 'POST',
                body: JSON.stringify(payload)
            });
            
            if (testRes.success || testRes.message === "Connection successful") {
                addNotification('Connection test successful ✓', 'success');
            } else {
                addNotification(`Test failed: ${testRes.message || 'Unknown error'}`, 'error');
            }
        } catch (err) {
            addNotification(`Test error: ${err.message}`, 'error');
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.host || !form.database || !form.username || !form.password) {
            addNotification('Please fill in all required fields.', 'warning');
            return;
        }
        setSaving(true);
        try {
            const meta = ENGINES.find(e => e.value === form.engine);
            const payload = {
                name: form.name,
                type: meta.type,
                host: form.host,
                port: Number(form.port),
                database: form.database,
                username: form.username,
                password: form.password
            };
        
            await apiFetch('/datasources', token, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            addNotification(`Connection "${form.name}" created successfully.`, 'success');
            onCreated();
        } catch (err) {
            addNotification(`Save failed: ${err.message}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-[#0d1526] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
                {/* Modal header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <DynamicIcon name="PlusCircle" className="w-5 h-5 text-blue-400" />
                        <h2 className="text-base font-semibold text-slate-100">New Database Connection</h2>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/5 text-slate-500 hover:text-slate-300 transition-colors">
                        <DynamicIcon name="X" className="w-4 h-4" />
                    </button>
                </div>

                {/* Form body */}
                <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    {/* Engine selector */}
                    <div className="grid grid-cols-3 gap-2">
                        {ENGINES.map(eng => (
                            <button
                                key={eng.value}
                                type="button"
                                onClick={() => handleEngineChange(eng.value)}
                                className={`py-2.5 px-3 rounded-xl border text-xs font-medium transition-all ${
                                    form.engine === eng.value
                                        ? 'bg-blue-600/30 border-blue-500 text-blue-300'
                                        : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20'
                                }`}
                            >
                                {eng.label}
                            </button>
                        ))}
                    </div>

                    <Field label="Connection Name" required>
                        <Input placeholder="e.g. Production Postgres" value={form.name} onChange={e => set('name', e.target.value)} />
                    </Field>

                    <Field label="Description">
                        <Input placeholder="Optional description" value={form.description} onChange={e => set('description', e.target.value)} />
                    </Field>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                            <Field label="Host" required>
                                <Input placeholder="localhost or IP" value={form.host} onChange={e => set('host', e.target.value)} />
                            </Field>
                        </div>
                        <Field label="Port" required>
                            <Input type="number" value={form.port} onChange={e => set('port', e.target.value)} />
                        </Field>
                    </div>

                    <Field label="Database Name" required>
                        <Input placeholder="database" value={form.database} onChange={e => set('database', e.target.value)} />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Username" required>
                            <Input placeholder="username" value={form.username} onChange={e => set('username', e.target.value)} />
                        </Field>
                        <Field label="Password" required>
                            <div className="relative">
                                <Input
                                    type={showPwd ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={form.password}
                                    onChange={e => set('password', e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd(!showPwd)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                >
                                    <DynamicIcon name={showPwd ? 'EyeOff' : 'Eye'} className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </Field>
                    </div>
                </div>

                {/* Footer actions */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-white/[0.02]">
                    <button
                        onClick={handleTest}
                        disabled={testing || !form.host || !form.database}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-600/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <DynamicIcon name={testing ? 'Loader2' : 'Zap'} className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
                        Test Connection
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors">
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <DynamicIcon name={saving ? 'Loader2' : 'Check'} className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                            Save Connection
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddConnectionModal;
